import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import { toast } from 'react-toastify';

import i18n from '@/i18n';

import { UserService } from '@/features/user/services/UserService';

import { TAppState } from '@/store/reducers/reducers';
import { IEndUserKeys } from '@/store/models/encryption.model';
import { addProfile } from '@/store/actions/profile.actions';
import { closeModal } from '@/store/actions/core.actions';
import { getSCAWallet } from '@/store/actions/sca.actions';
import { IProfileState } from '@/store/types/profile.types';
import { TAppError } from '@/store/types/base.state';
import { TCoreAction } from '@/store/types/modals.types';
import { TUsersAction, UsersActionTypes } from '@/store/types/users.types';
import { IPagination } from '@/store/types/search.types';

import { getTaxResidence } from '@/features/taxResidences/store/taxResidence.actions';

import { EncryptionService } from '@/services/encryption/EncryptionService';

import { IPostUser, IPutUser, ISearchUser, IUser, UserTypes } from './user.model';
import { TUserAction, UserActionTypes } from './user.types';

export const refreshUser = (
  userId: number,
  skipOthers?: boolean,
): ThunkAction<Promise<void>, TAppState, {}, TUserAction> => {
  const request = (): TUserAction => ({
    type: UserActionTypes.REFRESH_USER,
    userId,
  });
  const fail = (error: TAppError): TUserAction => ({
    type: UserActionTypes.REFRESH_USER_FAIL,
    userId,
    error,
    documentation: 'https://docs.treezor.com/guide/users/introduction.html#key-attributes',
  });
  const success = (user: IUser): TUserAction => ({
    type: UserActionTypes.REFRESH_USER_SUCCESS,
    user,
    shouldSkipToaster: true,
  });

  return async (dispatch: ThunkDispatch<TAppState, {}, TUserAction>, getState: () => TAppState): Promise<void> => {
    const { config } = getState().dashboardConfig;
    dispatch(request());
    try {
      const [user] = await UserService.searchUsers({ userId });

      dispatch(success(user));
      if (!skipOthers) {
        if (user.userTypeId === UserTypes.naturalPerson && config?.settings.sca) {
          dispatch(getSCAWallet(user.userId));
        }
        dispatch(getTaxResidence(user.userId));
      }
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const createUser = (
  userToCreate: IPostUser,
  passphrase?: string,
  onFinish?: (userId: number) => void,
): ThunkAction<Promise<void>, IProfileState, {}, TUserAction> => {
  const request = (): TUserAction => ({
    type: UserActionTypes.CREATE_USER,
  });
  const fail = (error: TAppError): TUserAction => ({
    type: UserActionTypes.CREATE_USER_FAIL,
    error,
    documentation: 'https://docs.treezor.com/guide/users/introduction.html#creation',
  });
  const success = (user: IUser): TUserAction => ({
    type: UserActionTypes.CREATE_USER_SUCCESS,
    user,
  });

  return async (dispatch: ThunkDispatch<IProfileState, {}, TUserAction>): Promise<void> => {
    dispatch(request());
    try {
      let userKeys: IEndUserKeys | undefined = undefined;
      if (passphrase) {
        const encryptionService = await EncryptionService.getInstance();
        userKeys = await encryptionService.createEndUserKeys(passphrase);
      }

      const newUser = await UserService.createUser(userToCreate, userKeys);

      dispatch(success(newUser));
      dispatch(addProfile({ userId: newUser.userId }));
      if (onFinish) {
        onFinish(newUser.userId);
      }
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const updateUser = (
  user: IPutUser,
  userId: number,
  onFinish?: () => void,
): ThunkAction<Promise<void>, IProfileState, {}, TUserAction | TCoreAction> => {
  const request = (): TUserAction => ({
    type: UserActionTypes.UPDATE_USER,
    userId,
  });
  const fail = (error: TAppError): TUserAction => ({
    type: UserActionTypes.UPDATE_USER_FAIL,
    userId,
    error,
  });
  const success = (user: IUser): TUserAction => ({
    type: UserActionTypes.UPDATE_USER_SUCCESS,
    user,
  });

  return async (dispatch: ThunkDispatch<IProfileState, {}, TUserAction | TCoreAction>): Promise<void> => {
    try {
      dispatch(request());
      const userUpdated = await UserService.updateUser(user, userId);
      dispatch(success(userUpdated));
      dispatch(closeModal());
      onFinish?.();
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const sendForgotPassword = (
  userId: number,
  email: string,
): ThunkAction<Promise<void>, IProfileState, {}, TUserAction> => {
  const request = (): TUserAction => ({
    type: UserActionTypes.SEND_FORGOT_PASSWORD,
    userId,
  });
  const fail = (error: TAppError): TUserAction => ({
    type: UserActionTypes.SEND_FORGOT_PASSWORD_FAIL,
    userId,
    error,
    documentation: 'https://docs.treezor.com/guide/dashboard/users.html#send-a-password-reset-email-to-a-user',
  });
  const success = (): TUserAction => ({
    type: UserActionTypes.SEND_FORGOT_PASSWORD_SUCCESS,
    userId,
    shouldSkipToaster: true,
  });

  return async (dispatch: ThunkDispatch<IProfileState, {}, TUserAction>): Promise<void> => {
    dispatch(request());
    try {
      await UserService.sendForgotPassword(email);

      toast.success(i18n.t('translation:pages.currentProfiles.profile.modalSendForgotPassword.toaster', { email }), {
        autoClose: 4000,
      });

      dispatch(success());
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const deactivateUser = (userId: number): ThunkAction<Promise<void>, IProfileState, {}, TUserAction> => {
  const request = (): TUserAction => ({
    type: UserActionTypes.DEACTIVATE_USER,
    userId,
  });
  const fail = (error: TAppError): TUserAction => ({
    type: UserActionTypes.DEACTIVATE_USER_FAIL,
    userId,
    error,
    documentation: 'https://docs.treezor.com/guide/users/introduction.html#deletion',
  });
  const success = (user: IUser): TUserAction => ({
    type: UserActionTypes.DEACTIVATE_USER_SUCCESS,
    user,
  });

  return async (dispatch: ThunkDispatch<IProfileState, {}, TUserAction>): Promise<void> => {
    dispatch(request());
    try {
      const userDeactivated = await UserService.deactivateUser(userId);
      dispatch(success(userDeactivated));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const searchUsersAgent = (
  pageNumber?: number,
  filters?: ISearchUser,
  shouldSkipToaster: boolean | undefined = undefined,
): ThunkAction<Promise<void>, {}, {}, TUsersAction> => {
  const request = (): TUsersAction => ({
    type: UsersActionTypes.GET_USERS,
  });
  const fail = (error: TAppError): TUsersAction => ({
    type: UsersActionTypes.GET_USERS_FAIL,
    error,
    documentation: 'https://docs.treezor.com/guide/users/introduction.html#key-attributes',
    shouldSkipToaster,
  });
  const success = (users: Array<IUser>, pagination: IPagination): TUsersAction => ({
    type: UsersActionTypes.GET_USERS_SUCCESS,
    users,
    pagination,
    shouldSkipToaster: shouldSkipToaster === undefined ? true : shouldSkipToaster,
  });

  return async (dispatch: ThunkDispatch<{}, {}, TUsersAction>): Promise<void> => {
    dispatch(request());
    try {
      let users = await UserService.searchUsers(filters || {}, pageNumber);
      let legalUsers: IUser[] = [];
      if (filters?.name) {
        const { name, ...otherFilters } = filters;
        legalUsers = await UserService.searchUsers({ ...(otherFilters || {}), legalName: filters.name }, pageNumber);
        users = [...(users || []), ...(legalUsers || [])];
        users = [...new Map(users.map((item) => [item.userId, item])).values()];
      }

      const totalRows =
        (legalUsers?.[0]?.totalRows > users[0]?.totalRows ? legalUsers?.[0]?.totalRows : users[0]?.totalRows) ||
        users.length;
      const pagination: IPagination = {
        currentPage: pageNumber || 1,
        totalPages: Math.trunc(totalRows / 50 || 0) + 1,
      };

      dispatch(success(users, pagination));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};
